import { useState } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/Modal';
import { ModalDelete } from '@/components/ModalDelete';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Switch } from '@/components/Switch';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { opportunityApi, opportunityErrors } from '@/pages/Opportunities/api';
import { useActionForm } from '@/pages/Opportunities/useActionForm';
import { businessTimezone, localDay, localTime } from '@/pages/Opportunities/dates';
import type { PlanningReminder } from '@/pages/Opportunities/types';

export interface PlanningReminderFormProps { item: PlanningReminder | null; date: string; onClose: () => void; onSaved: () => void; }
export const PlanningReminderForm = ({ item, date, onClose, onSaved }: PlanningReminderFormProps) => {
    const { t } = useTranslation('global');
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [remove, setRemove] = useState(false);
    const schema = z.object({ title: z.string().trim().min(1, 'opportunities.required'), startsAt: z.string().min(1, 'opportunities.required'), notes: z.string(), completed: z.boolean() });
    const { form, submit, busy, error } = useActionForm(schema, {
        title: item?.title ?? '', startsAt: item ? `${localDay(item.starts_at, item.timezone)}T${localTime(item.starts_at, item.timezone)}` : `${date}T09:00`, notes: item?.notes ?? '', completed: item?.is_completed ?? false,
    }, async values => {
        await opportunityApi.reminder(item?.id ?? null, { title: values.title, starts_at: values.startsAt, timezone: item?.timezone ?? businessTimezone, notes: values.notes || null, is_completed: values.completed, record_version: item?.record_version ?? 0, tender_id: item?.tender_id ?? null });
        onSaved(); onClose();
    });
    const { title, startsAt, notes, completed } = form.watch();
    const deleteReminder = async (): Promise<void> => {
        if(!item){
            return;
        }
        setDeleting(true);
        try{
            await opportunityApi.removeReminder(item.id, item.record_version);
            onSaved(); onClose();
        }
        catch(failure){
            setDeleteError(Object.values(opportunityErrors(failure)).join(' '));
        }
        finally{
            setDeleting(false);
        }
    };
    return <Modal isOpen title={t('opportunities.reminder')} onClose={onClose} onSubmit={submit} isSubmitting={busy} hasUnsavedChanges={form.formState.isDirty} submitLabel={t('opportunities.save')} submitDisabled={!title.trim() || !startsAt}>
        <div className="space-y-4">
            <Alert variant="info">{t('opportunities.reminder_explanation')}</Alert>
            {(error || deleteError) && <Alert variant="error">{error || deleteError}</Alert>}
            <Input label={t('opportunities.reminder_title')} required {...form.register('title')} error={form.formState.errors.title?.message} />
            <Input label={t('opportunities.reminder_date')} type="datetime-local" required {...form.register('startsAt')} error={form.formState.errors.startsAt?.message} />
            <Textarea label={t('opportunities.reminder_notes')} value={notes} onChange={event => form.setValue('notes', event.target.value, { shouldDirty: true })} />
            <Switch label={t('opportunities.reminder_completed')} checked={completed} onChange={value => form.setValue('completed', value, { shouldDirty: true })} />
            {item && <Button variant="danger" onClick={() => setRemove(true)}>{t('opportunities.delete')}</Button>}
            {item && <ModalDelete isOpen={remove} onClose={() => setRemove(false)} itemName={item.title} onConfirm={() => { void deleteReminder(); }} isDeleting={deleting} />}
        </div>
    </Modal>;
};
